import React from "react";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { adminApi } from "@/lib/api";
import { useAuth } from '@/context/AuthContext';
import { Switch } from "@/components/ui/switch";
import { categoryLabel } from "@/components/CategoryTag";
import SourceEditor, { sourceError } from "../../components/admin/SourceEditor";
import { timeAgo } from "@/lib/format";

const TYPE_COLORS = { PRIMARY: "#FF8A1F", OFFICIAL: "#4ea3ff", REPORTING: "#A7ABB0", RESEARCH: "#9b8cff", DATA: "#3ecf8e", DISCOVERY: "#f5c451" };
const TIER_LABELS = { PRIMARY: "PRIMARY", REPORTING: "REPORTING", RESEARCH: "RESEARCH", DISCOVERY: "DISCOVERY" };

export default function Sources() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editor, setEditor] = useState(null);
  const [busySource, setBusySource] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['sources'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
    qc.invalidateQueries({ queryKey: ['queue'] });
  };
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const { data: sources = [], isLoading, isError } = useQuery({ queryKey: ["sources"], queryFn: adminApi.sources });

  const toggle = async (id) => {
    if (busySource) return;
    setBusySource(id);
    try {
      await adminApi.toggleSource(id);
      qc.invalidateQueries({ queryKey: ["sources"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["queue"] });
    } catch (e) {
      toast.error(sourceError(e));
    } finally { setBusySource(null); }
  };
  const remove = async () => {
    if (busySource) return;
    setBusySource(deleteTarget.id); setDeleteError('');
    try { await adminApi.deleteSource(deleteTarget.id); setDeleteTarget(null); invalidate(); }
    catch (error) { setDeleteError(sourceError(error)); }
    finally { setBusySource(null); }
  };

  const runIngestion = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const refresh = await adminApi.refreshDiscovery();
      if (refresh.status === 'failed') throw new Error('RSS refresh failed');
      if (refresh.status === 'IN_PROGRESS') { toast.info('RSS refresh is already running.'); return; }
      const result = refresh.scout || {};
      setSyncResult(result);
      qc.invalidateQueries({ queryKey: ["sources"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["queue"] });
      toast.success(result.sources_failed ? "News Scout completed with source errors" : "News Scout completed");
    } catch (e) {
      toast.error("News Scout could not complete");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="max-w-[1100px]">
      <header className="mb-6">
        <div className="td-eyebrow mb-2" style={{ color: "#FF8A1F" }}>
          Ingestion
        </div>
        <h1 className="font-display font-extrabold text-white text-3xl tracking-tight">Sources</h1>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[#71767c] mt-1 font-mono-td text-xs">Registered RSS publishers for news discovery. Refreshing never dispatches paid generation.</p>
          {user?.role === 'admin' && <button
            onClick={runIngestion}
            disabled={syncing}
            className="inline-flex items-center gap-2 rounded border px-3 py-2 td-eyebrow transition-colors hover:bg-[#1B1E21] disabled:opacity-60"
            style={{ borderColor: "rgba(255,138,31,0.45)", color: "#FF8A1F", fontSize: "0.56rem" }}
          >
            <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing…" : "Refresh RSS & candidates"}
          </button>}
        </div>
        {syncResult && (
          <div className="mt-3 rounded border px-3 py-2 font-mono-td text-xs" style={{ borderColor: "rgba(62,207,142,0.25)", color: "#A7ABB0" }}>
            Checked {syncResult.sources_checked} sources · {syncResult.items_new} new items · {syncResult.items_duplicate} duplicates · {syncResult.sources_failed} failed
          </div>
        )}
      </header>

      {user?.role === 'admin' && <button onClick={() => setEditor({})} className="mb-4 rounded border border-[#FF8A1F] px-4 py-2 text-[#FFB347]">Add Source</button>}
      {editor && <SourceEditor key={editor.id || 'new'} source={editor.id ? editor : null} onCancel={() => setEditor(null)} onSaved={() => { setEditor(null); invalidate(); toast.success('Source saved'); }} />}
      {deleteTarget && <section role="dialog" aria-labelledby="delete-source-title" className="mb-5 p-5 rounded border border-[#f87171] bg-[#111315] text-white">
        <h2 id="delete-source-title">Delete {deleteTarget.name}?</h2>
        <p className="text-sm my-3">This source will stop discovery. Published articles, historical candidates, and audit records will be preserved.</p>
        <button disabled={!!busySource} onClick={remove} className="mr-5 text-[#f87171]">Confirm Delete</button>
        <button disabled={!!busySource} onClick={() => setDeleteTarget(null)}>Cancel</button>
        {deleteError && <p role="alert" className="mt-3 text-[#f87171]">{deleteError}</p>}
      </section>}
      {isError && <p role="alert" className="mb-4 text-[#f87171]">Sources unavailable. Please refresh and try again.</p>}
      {!isLoading && !isError && !sources.length && <p className="mb-4 text-[#A7ABB0]">No sources registered.</p>}
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#111315" }}>
        <div className="hidden md:grid grid-cols-[1fr_105px_105px_90px_90px_110px_110px_60px] gap-3 px-5 py-3 border-b td-eyebrow" style={{ borderColor: "rgba(255,255,255,0.08)", color: "#71767c", fontSize: "0.52rem" }}>
          <span>Source</span>
          <span>Type</span>
          <span>Tier</span>
          <span>Section</span>
          <span>Reliability</span>
          <span>Last Checked</span>
          <span>Last Success</span>
          <span>On</span>
        </div>
        {isLoading ? (
          <p className="p-5 text-[#A7ABB0]">Loading…</p>
        ) : (
          sources.map((s) => (
            <div
              key={s.id}
              className="grid grid-cols-1 md:grid-cols-[1fr_105px_105px_90px_90px_110px_110px_60px] gap-2 md:gap-3 px-5 py-4 border-b last:border-0 items-center"
              style={{ borderColor: "rgba(255,255,255,0.06)" }}
              data-testid={`source-row-${s.id}`}
            >
              <div className="min-w-0">
                <div className="text-white font-display font-medium text-sm truncate">{s.name}</div>
                <div className="font-mono-td truncate" style={{ color: "#71767c", fontSize: "0.62rem" }}>{s.feed_url || s.endpoint}</div>
                <div className="text-xs text-[#A7ABB0] mt-2">{s.feed_health?.replaceAll('_', ' ') || 'Health unknown'}{s.refresh_overdue ? ' · refresh overdue' : ''}</div>
                <div className="text-xs text-[#71767c] mt-1">Newest article: {s.latest_publication_at ? timeAgo(s.latest_publication_at) : 'Unavailable'} · Missing dates: {s.missing_date_count ?? '—'}</div>
                {user?.role === 'admin' && <div className="flex gap-4 mt-2 text-xs">
                  <button disabled={!!busySource} onClick={() => setEditor(s)} className="text-[#FFB347]" aria-label={`Edit ${s.name}`}>Edit</button>
                  <button disabled={!!busySource} onClick={() => { setDeleteError(''); setDeleteTarget(s); }} className="text-[#f87171]" aria-label={`Delete ${s.name}`}>Delete</button>
                </div>}
                {(s.last_error || s.error_state) && <div className="text-[#f87171] mt-1" style={{ fontSize: "0.68rem" }}>⚠ {s.last_error || s.error_state}</div>}
              </div>
              <span className="td-eyebrow" style={{ color: TYPE_COLORS[s.source_type] || "#A7ABB0", fontSize: "0.52rem" }}>{s.source_type}</span>
              <span className="td-eyebrow" style={{ color: TYPE_COLORS[s.source_tier] || "#A7ABB0", fontSize: "0.52rem" }}>{TIER_LABELS[s.source_tier] || s.source_tier || "DISCOVERY"}</span>
              <span className="td-eyebrow" style={{ color: "#A7ABB0", fontSize: "0.55rem" }}>{categoryLabel(s.category)}</span>
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-12 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
                  <div className="h-full" style={{ width: `${s.reliability}%`, background: s.reliability >= 85 ? "#3ecf8e" : s.reliability >= 70 ? "#f5c451" : "#f87171" }} />
                </div>
                <span className="font-mono-td" style={{ color: "#A7ABB0", fontSize: "0.62rem" }}>{s.reliability}</span>
              </div>
              <span className="font-mono-td" style={{ color: "#71767c", fontSize: "0.62rem" }}>{s.last_checked_at ? timeAgo(s.last_checked_at) : s.last_checked ? timeAgo(s.last_checked) : "—"}</span>
              <span className="font-mono-td" style={{ color: "#71767c", fontSize: "0.62rem" }}>{s.last_success_at ? timeAgo(s.last_success_at) : s.last_success ? timeAgo(s.last_success) : "—"}</span>
              <Switch disabled={user?.role !== 'admin' || !!busySource} checked={s.enabled} onCheckedChange={() => toggle(s.id)} data-testid={`source-toggle-${s.id}`} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
